import React, { useState } from 'react';
import { object, string, func, oneOf } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { ExternalLink, IconMail } from '../../components';
import TeamAmPortrait from '../../containers/TeamAmPage/TeamAmPortrait/TeamAmPortrait';
import TeamAmContactModal from '../../containers/TeamAmPage/TeamAmContactModal/TeamAmContactModal';

import css from './AccountManagerCard.module.css';

export const SEARCH_VARIANT = 'search';
export const LISTING_VARIANT = 'listing';

/**
 * The account manager who owns a desk, shown on a public page.
 *
 * Two places use it, and they differ only in wording and shape:
 *
 *   search   Above the first result on the search page, when the category
 *            filter has resolved to exactly one vertical. The page makes it
 *            sticky, so it stays with the reader as they scroll the results.
 *            No frame: a bare row of round photo, name · market and button,
 *            reading as part of the category heading above it.
 *   listing  In the listing page's right rail, under the Enquire and Debt
 *            Advisory buttons (after the description on mobile). Portrait crop,
 *            plus a supporting line saying the manager holds the proof
 *            documents.
 *
 * RENDERS NOTHING WITHOUT A MANAGER. Only some categories have a desk, and the
 * agreed behaviour is to leave the card out rather than draw a placeholder — so
 * every caller can pass whatever it found and let this decide.
 *
 * This card is informational and outbound. It does not replace Enquire, which
 * stays the primary action on a listing.
 *
 * WHY IT OPENS THE DIALOG RATHER THAN A BARE mailto:
 *   TeamAmContactModal is already the one way to reach a manager on this site,
 *   and it offers WhatsApp and phone as well as email. A bare mailto would be a
 *   second, narrower path to the same person. The subject line the spec asked
 *   for is passed through to the dialog's email row instead, so nothing is
 *   lost.
 *
 *   On a public page the reader is often signed out, and the server withholds
 *   the phone number from anybody who is — so for a visitor the dialog shows
 *   the email row alone, which is exactly the bare mailto behaviour.
 *
 * Managers with a `contactUrl` (Jack Collins) never open the dialog. They get a
 * link out to that page, the same as on the Team page and the Subscriptions
 * page.
 */
const AccountManagerCard = props => {
  const {
    className,
    rootClassName,
    manager,
    variant,
    eyebrow,
    mailtoSubject,
    whatsappText,
    onManageDisableScrolling,
    intl,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!manager) {
    return null;
  }

  const isListing = variant === LISTING_VARIANT;

  // The search card's frame is a circle, which a square source fills exactly.
  // The listing card's frame is the tall 4:5 crop the portrait was made for.
  // Each falls back to the other picture, because a manager may have only one.
  const photo = isListing
    ? manager.photo || manager.squarePhoto
    : manager.squarePhoto || manager.photo;

  // `vertical` is the manager's market, written for a person to read — "Head of
  // Automotive". On the search card a row with none falls back to the plain
  // role. The listing card never prints the role, so there it is left out.
  const category =
    manager.vertical ||
    (isListing ? null : intl.formatMessage({ id: 'AccountManagerCard.role' }));

  // The same envelope the dialog puts on its email row, so the button and what
  // it opens read as one thing.
  //
  // The search card says "Chat with James" at every width: it sits in its own
  // row under the category heading, so a phone has room for the full label.
  const getInTouch = isListing ? (
    <>
      {/* rootClassName, not className: it REPLACES IconMail's own .root rather
          than sitting beside it. Both are single-class selectors, so passing
          className would leave two rules setting width with equal specificity
          and let bundle order decide the size. */}
      <IconMail rootClassName={css.buttonIcon} />
      {/* "Chat" on a phone, "Chat to Freddie" from --viewportMedium up.

          Both labels are in the markup and CSS hides one, rather than choosing
          in JS. This card is rendered on the server, where there is no viewport
          to measure — picking there would either need a browser-only second
          pass or would risk the server and the browser disagreeing on hydration.
          display: none also takes the hidden one out of the accessibility tree,
          so a screen reader still hears exactly one label. */}
      <span className={css.buttonLabelShort}>
        <FormattedMessage id="AccountManagerCard.chatToShort" />
      </span>
      <span className={css.buttonLabelLong}>
        <FormattedMessage
          id="AccountManagerCard.chatTo"
          values={{ firstName: manager.firstName }}
        />
      </span>
    </>
  ) : (
    <>
      <IconMail rootClassName={css.buttonIcon} />
      <span>
        <FormattedMessage id="AccountManagerCard.chatWith" values={{ firstName: manager.firstName }} />
      </span>
    </>
  );

  const button = manager.contactUrl ? (
    <ExternalLink className={css.button} href={manager.contactUrl}>
      {getInTouch}
    </ExternalLink>
  ) : (
    <button className={css.button} type="button" onClick={() => setIsModalOpen(true)}>
      {getInTouch}
    </button>
  );

  const classes = classNames(rootClassName || css.root, className, {
    [css.search]: !isListing,
    [css.listing]: isListing,
  });

  return (
    <div className={classes}>
      {/* The eyebrow is a line of its own across the top, above the picture: it
          labels the whole card, not the name beside it.

          Only the listing card has one. The search card sits directly under the
          category heading, which already says what it is. */}
      {eyebrow ? <p className={css.eyebrow}>{eyebrow}</p> : null}

      <div className={css.body}>
        {/* The round frame on the search card lives on this wrapper, not on
            TeamAmPortrait, whose own 4:5 rounded rectangle is simply clipped
            away by it. The grayscale filter is here too: the pictures are
            stored in colour and every surface that wants the brand treatment
            applies it itself. See docs/account-managers-logic.md. */}
        <span className={css.avatar}>
          <TeamAmPortrait
            className={css.portrait}
            photo={photo}
            name={manager.name}
            initials={manager.initials}
          />
        </span>

        <div className={css.text}>
          <h3 className={css.name}>{manager.name}</h3>
          {/* "James Goldsztajn · Head of Automotive" on one line on the desktop
              search card. On a phone the two lines stack and CSS hides the
              dot. */}
          {category && !isListing ? (
            <span className={css.separator} aria-hidden="true">
              ·
            </span>
          ) : null}
          {category ? <p className={css.category}>{category}</p> : null}
        </div>

        <div className={css.action}>{button}</div>
      </div>

      {isListing ? (
        <p className={css.supporting}>
          <FormattedMessage
            id="AccountManagerCard.listingSupportingText"
            values={{ firstName: manager.firstName }}
          />
        </p>
      ) : null}

      <TeamAmContactModal
        isOpen={isModalOpen}
        member={manager}
        mailtoSubject={mailtoSubject}
        whatsappText={whatsappText}
        onClose={() => setIsModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

AccountManagerCard.defaultProps = {
  className: null,
  rootClassName: null,
  manager: null,
  variant: SEARCH_VARIANT,
  eyebrow: null,
  mailtoSubject: null,
  whatsappText: null,
};

AccountManagerCard.propTypes = {
  className: string,
  rootClassName: string,
  /**
   * The manager, in the shape toPublicManager builds on the server. Null is a
   * normal value: the card then renders nothing.
   */
  manager: object,
  variant: oneOf([SEARCH_VARIANT, LISTING_VARIANT]),
  /**
   * The small gold line across the top of the card. Left out entirely when not
   * given — the search card passes nothing.
   */
  eyebrow: string,
  /** Pre-fills the subject of the dialog's email link. */
  mailtoSubject: string,
  /** Pre-fills the dialog's WhatsApp message. */
  whatsappText: string,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(AccountManagerCard);
